import { HotelCard, HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { HotelCardPricing } from "@website/accommodation-ui/hotel-card-new/hotel-card-action/hotel-card-pricing";
import { HotelCardReservationInfo } from "@website/accommodation-ui/hotel-card-new/hotel-card-action/hotel-card-reservation-info";
import ErrorArt from "@website/accommodation-ui/hotel-cities-main-content/assets/error-art.png";
import NotFoundArt from "@website/accommodation-ui/hotel-cities-main-content/assets/not-found-art.png";
import { useHotelCitiesMainContent } from "@website/accommodation-ui/hotel-cities-main-content/hotel-cities-main-content.hook";
import { HotelCitiesMainContentPropertiesType } from "@website/accommodation-ui/hotel-cities-main-content/hotel-cities-main-content.types";
import { HotelSuggestionCarousel } from "@website/accommodation-ui/hotel-cities-main-content/hotel-suggestion-carousel";
import { HotelLastSecondCityPage } from "@website/accommodation-ui/hotel-last-second";
import { HotelCityTitle } from "@website/accommodation-ui/hotel-list-header";
import { HotelCardSkeleton } from "@website/accommodation-ui/hotel-pagination/hotel-card-skeleton";
import { HotelSnappPay } from "@website/accommodation-ui/hotel-snapp-pay";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import {
  USER_TRACKING_EVENTS,
  WEBENGAGE_EVENTS
} from "@website/tracking/tracking.types";
import { addEventToWebengage } from "@website/tracking/tracking.webengage";
import {
  ApplicationNameType,
  addEventToTracking,
  mapProductForUserTracking
} from "@website/tracking/user";
import { Button } from "@website/ui/button";
import { Modal } from "@website/ui/modal";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { AccommodationBanner } from "@website/widget/accommodation-banner";
import { BannerSlide } from "@website/widget/banner-slide";
import { ReserveCommentPropertiesType } from "@website/widget/reserve-comment";
import { ReserveCommentPopupScroll } from "@website/widget/reserve-comment-popup-scroll";
import { useReserveGalleryDetailContainer } from "@website/widget/reserve-gallery";
import {
  ReserveGalleryDetail,
  ReserveGallerySingle
} from "@website/widget/reserve-gallery-detail";
import { ReserveMap } from "@website/widget/reserve-map";
import last from "lodash-es/last";
import map from "lodash-es/map";
import omit from "lodash-es/omit";
import orderBy from "lodash-es/orderBy";
import range from "lodash-es/range";
import toNumber from "lodash-es/toNumber";
import uniq from "lodash-es/uniq";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import {
  FC,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { hotelCardOnClick } from "../hotel-card-new/hotel-card-link";

const PageMessage = dynamic(
  () => import("./hotel-page-message").then(({ PageMessage }) => PageMessage),
  {
    ssr: false
  }
);

const PageNotFound = ({
  cityTitle,
  hasFilters,
  onActionClick
}: {
  cityTitle: string;
  hasFilters: boolean;
  onActionClick: () => void;
}) => {
  const router = useRouter();
  return (
    <PageMessage
      actionIcon={hasFilters ? <MaterialIcon name="delete_outline" /> : null}
      actionTitle={hasFilters ? "حذف فیلترها" : "تغییر مقصد"}
      artSrc={NotFoundArt.src}
      caption={
        router.query.locationType === "category" && !hasFilters ? (
          <>
            متاسفانه در حال حاضر اقامتگاهی متناسب با دسته‌بندی انتخابی شما یافت
            نشد.
          </>
        ) : hasFilters ? (
          <>
            متاسفانه در حال حاضر هتل و اقامتگاهی متناسب با فیلتر های انتخابی شما
            وجود ندارد .
          </>
        ) : router?.pathname?.includes("map") ? (
          <span>
            متاسفانه هتل و اقامتگاه فعالی در این محدوده یافت نشد. لطفا شهرهای
            دیگر را جستجو کنید.
          </span>
        ) : (
          //TODO
          <>
            متاسفانه هتل و اقامتگاه فعالی در شهر{" "}
            <span className="text-on-surface-high-emphasis font-medium">
              &quot;{cityTitle}&quot;
            </span>{" "}
            یافت نشد. لطفا شهرهای دیگر را جستجو کنید.
          </>
        )
      }
      message="نتیجه‌ای یافت نشد."
      onActionClick={onActionClick}
    />
  );
};

const PageError = () => {
  const router = useRouter();
  const onActionClick = useCallback(
    () => (window.location.href = getOrigin()),
    [router]
  );
  return (
    <PageMessage
      actionIcon={<MaterialIcon name="arrow_forward" />}
      actionTitle="بازگشت به صفحه‌ی اصلی"
      artSrc={ErrorArt.src}
      caption="متاسفانه اطلاعاتی یافت نشد."
      message="خطایی رخ داده است!"
      onActionClick={onActionClick}
    />
  );
};

const HotelCitiesMainContentComponent: FC<
  HotelCitiesMainContentPropertiesType
> = (properties) => {
  const {
    size,
    cityTitle,
    hotelsSuggested,
    snappPayData,
    bannerSlideList,
    inlineBannerList,
    isBannersLoading,
    hasLoadMoreButton = false,
    lastSecondHotelsList,
    lastSecondHotelsLoading,
    accommodationData,
    className,
    targetLink,
    onMouseEnter,
    onMouseLeave,
    selectedHotelId,
    totalItem,
    reviewData
  } = properties;
  const {
    hotelList,
    loading,
    hasNoResults,
    hasError,
    hasSort,
    hasFilters,
    hasHotelSuggestion,
    suggestionOrder,
    onLoadMore,
    remainHotels,
    handleNotFoundAction
  } = useHotelCitiesMainContent(properties);
  const router = useRouter();
  const { isMobile, isTablet } = useViewPort();
  const [clickLoading, setClickLoading] = useState(0);
  const availableHotels = useMemo(
    () => hotelList.filter((hotel) => hotel.hotelDetail?.isAvailable),
    [hotelList]
  );

  useEffect(() => {
    const [hotelInfo] = hotelList;
    if (!hotelInfo?.hotelDetail.cityId || loading) return;

    const availableHotels = hotelList.filter(
      (hotel) => hotel.hotelDetail?.isAvailable
    );

    addEventToWebengage(
      process.env.NEXT_PUBLIC_APP_NAME === "hotel-international"
        ? WEBENGAGE_EVENTS.IH_cityVisit
        : WEBENGAGE_EVENTS.CITY_VISIT,
      {
        "page title": document.title,
        "city name": hotelInfo?.hotelDetail.cityTitle,
        "check in date": router.query.date_from || "",
        "check out date": router.query.date_to || "",
        "page url": window.location.href
      }
    );

    addEventToTracking(USER_TRACKING_EVENTS.PAGE_VISIT_CITY, {
      city: hotelInfo?.hotelDetail.cityTitle,
      city_id: hotelInfo?.hotelDetail.cityId,
      date_from: new Date(router.query.date_from as string),
      date_to: new Date(router.query.date_to as string),
      available_hotels_id: availableHotels.map((hotel) => hotel.id),
      page: last(orderBy(uniq(map(availableHotels, "page")))),
      product: mapProductForUserTracking(
        process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType
      ),
      last_minute_loaded: lastSecondHotelsList?.length ? 1 : 0,
      count_of_last_minute_cards: lastSecondHotelsList?.length,
      blue_box_loaded: hotelsSuggested?.length ? 1 : 0,
      count_of_blue_box_cards: hotelsSuggested?.length
    });
  }, [
    hotelList,
    hotelsSuggested?.length,
    lastSecondHotelsList?.length,
    loading,
    router
  ]);

  const onClose = useCallback(
    () =>
      router.replace(
        {
          pathname: router?.pathname,
          query: omit(router?.query, ["modal", "selectedHotelId"])
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [router]
  );

  const selectedHotel = useMemo(
    () =>
      hotelList.find((hotel) =>
        ["villa"].includes(process.env.NEXT_PUBLIC_APP_NAME ?? "") &&
        router?.query?.["modal"] === "review"
          ? hotel.hotelDetail?.propertyId === Number(selectedHotelId)
          : hotel.id === Number(selectedHotelId)
      ) as HotelInfo,
    [hotelList, router?.query, selectedHotelId]
  );

  const { containerProps } = useReserveGalleryDetailContainer({
    templateNumber: 4,
    galleryLength: selectedHotel?.gallery?.length ?? 0
  });
  return hasError ? (
    <PageError />
  ) : hasNoResults ? (
    <div>
      {accommodationData?.data?.[cityTitle] &&
        isChannel(["website", "web", "jek"]) && (
          <AccommodationBanner
            cityTitle={accommodationData?.cityTitle}
            onclick={accommodationData?.onClick}
          />
        )}
      <PageNotFound
        cityTitle={cityTitle}
        hasFilters={hasFilters}
        onActionClick={handleNotFoundAction}
      />
    </div>
  ) : (
    <>
      <HotelLastSecondCityPage
        cityTitle={cityTitle}
        lastSecondHotels={lastSecondHotelsList}
        loading={lastSecondHotelsLoading}
      />
      {!isChannel(["ads"]) && (
        <HotelSnappPay isEligible={snappPayData?.isEligible} />
      )}
      <section
        className={`${
          className ||
          (size === "small"
            ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            : "flex w-full flex-col items-center gap-4 lg:gap-6")
        }`}
      >
        {hasHotelSuggestion &&
          !hasFilters &&
          !hasSort &&
          hotelList?.length > 0 && (
            <section
              className={`col-span-full w-full ${
                suggestionOrder === 1
                  ? "order-1"
                  : suggestionOrder === 2
                    ? "order-2"
                    : suggestionOrder === 3
                      ? "order-3"
                      : ""
              } ${size === "small" ? "sm:order-2 lg:order-3" : ""}`}
            >
              <HotelSuggestionCarousel hotelsSuggested={hotelsSuggested} />
            </section>
          )}
        <div className="col-span-1 -mt-5 block self-start sm:col-span-2 lg:col-span-3 lg:mt-0 lg:hidden">
          <HotelCityTitle
            city={cityTitle}
            hasCityPrefix={router.query.locationType !== "category"}
            loading={loading}
            totalItem={totalItem}
          />
        </div>
        {hotelList?.map((hotelInfo, index) => (
          <Fragment key={hotelInfo?.id}>
            {(bannerSlideList?.length ?? 0) > 0 && index === 1 && (
              <div
                className={
                  process.env.NEXT_PUBLIC_APP_NAME === "villa"
                    ? "-order-1 col-span-1 gap-4 sm:col-span-2 lg:col-span-3"
                    : "w-full"
                }
              >
                <BannerSlide
                  className="w-full !p-0"
                  height={isMobile ? 87 : isTablet ? 175 : 70}
                  isLoading={isBannersLoading}
                  list={bannerSlideList ?? []}
                  width={isMobile ? 378 : isTablet ? 760 : 812}
                  shouldRedirectUsingJs
                />
              </div>
            )}
            {accommodationData?.data?.[cityTitle] &&
              index ===
                (availableHotels.length > 0
                  ? availableHotels?.length - 1
                  : 0) &&
              isChannel(["website", "web", "jek"]) && (
                <div
                  className="w-full"
                  style={{
                    order:
                      availableHotels.length > 0 ? availableHotels?.length : 0
                  }}
                >
                  <AccommodationBanner
                    cityTitle={accommodationData?.cityTitle}
                    onclick={accommodationData?.onClick}
                  />
                </div>
              )}
            {(inlineBannerList?.length ?? 0) > 0 && index === 3 && (
              <div className="order-4 w-full">
                <BannerSlide
                  className="w-full !p-0"
                  height={72}
                  isLoading={isBannersLoading}
                  list={inlineBannerList ?? []}
                  shouldRedirectUsingJs
                />
              </div>
            )}
            <article className="w-full" style={{ order: index }}>
              <HotelCard
                hotelInfo={hotelInfo}
                index={index + 1}
                key={hotelInfo?.hotelDetail?.hotelTitle}
                loading={loading}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                showMap={
                  Boolean(hotelInfo?.location?.lat) &&
                  Boolean(hotelInfo?.location?.lng)
                }
                size={size}
                snappPayData={snappPayData}
                targetLink={targetLink}
              />
            </article>
          </Fragment>
        ))}
        <Modal
          dialogClassName="lg:!h-[640px] lg:!w-[720px] overflow-y-scroll rounded-xl  md:rounded-0 !h-[90vh] md:!h-full md:[clip-path:inset(0_round_1rem)]"
          headerStyle="!h-16 pb-2"
          onClose={onClose}
          open={
            selectedHotel &&
            router?.query?.["modal"] === "review" &&
            (reviewData?.comments?.length ?? 0) > 0
          }
          showCloseButton
          title={numberConvert(selectedHotel?.hotelDetail?.hotelTitle, {
            locale: router.locale || "fa"
          })}
          titleClassName="line-clamp-2"
          usePortal
        >
          <ReserveCommentPopupScroll
            comments={
              reviewData?.comments as Array<ReserveCommentPropertiesType>
            }
            onClick={hotelCardOnClick({
              hotelInfo: selectedHotel,
              index: hotelList?.findIndex(
                (hotel) => hotel.id === selectedHotel?.id
              ),
              router,
              setClickLoading,
              hash: "#hotel_comments"
            })}
            rate={reviewData?.rate}
            size="small"
          />
          <div className="sticky bottom-0 -mx-4 -mb-4 mt-auto">
            <div className="-mb-6 flex w-full flex-row bg-white p-4 md:justify-end">
              <Button
                className="!hidden md:!block"
                color="neutral"
                onClick={onClose}
                text="بستن"
                variant="text"
              />
              <div className="flex w-full flex-col gap-4 md:w-auto">
                <div className="flex flex-row items-center justify-between md:hidden">
                  {process.env.NEXT_PUBLIC_APP_NAME === "villa" ? (
                    <HotelCardReservationInfo
                      {...selectedHotel?.reservationInfo}
                      roomTitle={selectedHotel?.hotelDetail?.roomTitle}
                      size={size}
                    />
                  ) : (
                    <div className="text-caption">{`${numberConvert(
                      selectedHotel?.reservationInfo?.nightCount,
                      { locale: router.locale }
                    )} شب`}</div>
                  )}
                  <HotelCardPricing
                    className="text-subtitle-1"
                    {...selectedHotel?.pricing}
                    nightCount={selectedHotel?.reservationInfo?.nightCount}
                    size={size}
                  />
                </div>
                <Button
                  className="!w-full md:!w-auto"
                  color="primary"
                  loading={clickLoading === selectedHotel?.id}
                  onClick={hotelCardOnClick({
                    hotelInfo: selectedHotel,
                    index: hotelList?.findIndex(
                      (hotel) => hotel.id === selectedHotel?.id
                    ),
                    router,
                    setClickLoading
                  })}
                  text={
                    ["villa"].includes(process.env.NEXT_PUBLIC_APP_NAME ?? "")
                      ? "مشاهده اقامتگاه و رزرو"
                      : "مشاهده هتل و رزرو"
                  }
                />
              </div>
            </div>
          </div>
        </Modal>
        {selectedHotel && (
          <Modal
            dialogClassName="lg:!h-[640px] lg:!w-[720px] lg:!overflow-hidden overflow-y-scroll rounded-xl  md:rounded-0 !h-[90vh] md:!h-full"
            onClose={onClose}
            open={selectedHotel && router?.query?.["modal"] === "map"}
            showCloseButton
            title={`موقعیت های مکانی و دسترسی های ${numberConvert(
              selectedHotel?.hotelDetail?.hotelTitle,
              { locale: router.locale }
            )}`}
            titleClassName="!text-subtitle-1 md:!text-headline-6"
          >
            <ReserveMap
              mapOptions={{
                center: {
                  lat: toNumber(selectedHotel?.location?.lat),
                  lng: toNumber(selectedHotel?.location?.lng)
                }
              }}
              marker={{
                lat: toNumber(selectedHotel?.location?.lat),
                lng: toNumber(selectedHotel?.location?.lng)
              }}
              showDescription={false}
              tile={
                process.env.NEXT_PUBLIC_APP_NAME === "hotel-domestic"
                  ? "smapp"
                  : "global"
              }
              title=""
              type="hotel"
            />
            <div className="mt-auto flex flex-row justify-end gap-4 pt-4">
              <Button
                color="neutral"
                onClick={onClose}
                text="بستن"
                variant="text"
              />
              <Button
                className="w-auto"
                color="primary"
                loading={clickLoading === selectedHotel?.id}
                onClick={hotelCardOnClick({
                  hotelInfo: selectedHotel,
                  index: hotelList?.findIndex(
                    (hotel) => hotel.id === selectedHotel?.id
                  ),
                  router,
                  setClickLoading
                })}
                text={
                  ["villa"].includes(process.env.NEXT_PUBLIC_APP_NAME ?? "")
                    ? "مشاهده اقامتگاه و رزرو"
                    : "مشاهده هتل و رزرو"
                }
              />
            </div>
          </Modal>
        )}
        <Modal
          dialogClassName="lg:!h-[640px] lg:!w-[720px] overflow-y-scroll rounded-xl  md:rounded-0 !h-[90vh] !max-h-full md:!h-full"
          headerStyle="!h-16 pb-2"
          onClose={onClose}
          open={
            selectedHotel &&
            (router?.query?.["modal"] === "gallery" ||
              router?.query?.["modal"] === "gallery-details")
          }
          showCloseButton
          title={numberConvert(selectedHotel?.hotelDetail?.hotelTitle, {
            locale: router.locale || "fa"
          })}
          titleClassName="line-clamp-2"
          usePortal
        >
          <div className="grid h-full grid-cols-2 gap-2 overflow-y-auto p-2 lg:px-[calc((100%-750px)/2)] lg:py-6">
            {containerProps?.map(({ index, rowCount }) => (
              <ReserveGalleryDetail
                gallery={selectedHotel?.gallery ?? []}
                index={index}
                key={`HotelReserveGalleryDetailContainer-${++index}`}
                rowCount={rowCount}
              />
            ))}
          </div>
          <ReserveGallerySingle gallery={selectedHotel?.gallery ?? []} />
          <div className="mt-auto flex flex-row justify-end gap-4 pt-4">
            <Button
              color="neutral"
              onClick={onClose}
              text="بستن"
              variant="text"
            />
            <Button
              className="w-auto"
              color="primary"
              loading={clickLoading === selectedHotel?.id}
              onClick={hotelCardOnClick({
                hotelInfo: selectedHotel,
                index: hotelList?.findIndex(
                  (hotel) => hotel.id === selectedHotel?.id
                ),
                router,
                setClickLoading
              })}
              text={
                ["villa"].includes(process.env.NEXT_PUBLIC_APP_NAME ?? "")
                  ? "مشاهده اقامتگاه و رزرو"
                  : "مشاهده هتل و رزرو"
              }
            />
          </div>
        </Modal>
      </section>
      {hasLoadMoreButton && remainHotels > 0 && (
        <Button
          color="secondary"
          fullWidth
          onClick={onLoadMore}
          variant="round-outline"
        >
          مشاهده {numberConvert(remainHotels, { locale: "fa" })} هتل دیگر
        </Button>
      )}
      {loading && (
        <div
          className={
            process.env.NEXT_PUBLIC_APP_NAME === "villa"
              ? `grid grid-cols-1 gap-2 p-2 md:grid-cols-2 ${
                  router?.pathname?.includes("map")
                    ? "lg:grid-cols-2"
                    : "lg:grid-cols-3"
                }`
              : "flex flex-col gap-2"
          }
        >
          {range(6).map((indexKey) => (
            <HotelCardSkeleton
              key={indexKey}
              size={
                process.env.NEXT_PUBLIC_APP_NAME === "villa"
                  ? "small"
                  : "responsive"
              }
            />
          ))}
        </div>
      )}
    </>
  );
};

export const HotelCitiesMainContent = memo(
  HotelCitiesMainContentComponent,
  deepCompare
);
